import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Navbar from 'react-bootstrap/Navbar';
import Container from 'react-bootstrap/Container';
import { LinkContainer } from 'react-router-bootstrap';
import Nav from 'react-bootstrap/Nav';
import NavDropdown from 'react-bootstrap/NavDropdown';
import { useAuth } from '../util/auth';
import { useLocation } from '../util/router';
import { getActiveItems } from '../util/config';
import './NavbarCustom.scss';
import { showForAdminView } from '../util/util';
import { RULES, SCREEN } from '../util/Constant';
import { useGetPersonnel } from '../util/db';
import { getFuelAppUrl } from '../util/cross-app-urls';

const ShowLink = ({ to, children, show }) => {
  if (!show) return null;

  return (
    <LinkContainer to={to}>
      <NavDropdown.Item active={false}>{children}</NavDropdown.Item>
    </LinkContainer>
  );
};

ShowLink.propTypes = {
  to: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  show: PropTypes.bool.isRequired
};

const ShowExtLink = ({ to, children, show = true }) => {
  if (!show) return null;

  // Determine if this is a truly external URL (different domain)
  const isExternalUrl = to && (to.startsWith('http://') || to.startsWith('https://'));

  return (
    <NavDropdown.Item
      active={false}
      href={to}
      target={isExternalUrl ? '_blank' : undefined}
      rel={isExternalUrl ? 'noopener noreferrer' : undefined}
    >
      {children}
    </NavDropdown.Item>
  );
};

ShowExtLink.propTypes = {
  to: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  show: PropTypes.bool
};

function NavbarCustom({
  bg = 'light',
  variant = 'light',
  expand = 'md',
  logo = 'https://via.placeholder.com/150',
  logoAncillary = 'https://via.placeholder.com/150'
}) {
  const pathsWithMainLogo = [
    '',
    '/',
    '/dashboard',
    '/chart',
    '/master',
    '/notifications',
    '/settings',
    '/settings/general',
    '/settings/password',
    '/auth/signin',
    '/print-calculation'
  ];

  const auth = useAuth();
  const activeItems = getActiveItems(window.location.hostname, window.location.href);
  const location = useLocation();

  const [logoToUse, setLogoToUse] = useState(
    pathsWithMainLogo.includes(location.pathname) ? logo : logoAncillary
  );

  const { data: personnel } = useGetPersonnel();

  const [permissionData, setPermissionData] = useState([]);
  const [fuelAppUrl, setFuelAppUrl] = useState(null);

  // Get Fuel app URL for cross-linking
  useEffect(() => {
    const url = getFuelAppUrl();
    setFuelAppUrl(url);
  }, []);

  useEffect(() => {
    setLogoToUse(pathsWithMainLogo.includes(location.pathname) ? logo : logoAncillary);
  }, [location?.pathname]);

  useEffect(() => {
    setLogoToUse(pathsWithMainLogo.includes(location.pathname) ? logo : logoAncillary);
  }, [location.pathname]);

  useEffect(() => {
    if (!personnel || !auth?.user?.email || !auth?.permissionData) return;

    const matchingUser = personnel.find((person) => person.email === auth.user.email);

    if (!matchingUser) return;
    const userRole = matchingUser.permission;
    const rolePermissionData = auth.permissionData[userRole];
    setPermissionData(rolePermissionData || null);
  }, [personnel, auth]);

  return (
    <Navbar bg={bg} variant={variant} expand={expand}>
      <Container className="navbar-brand-left-aligned">
        <LinkContainer to="/">
          <Navbar.Brand>
            <img className="d-inline-block align-top" src={logoToUse} alt="Logo" height="55" />
          </Navbar.Brand>
        </LinkContainer>
        <Navbar.Toggle aria-controls="navbar-nav" className="border-0" />
        <Navbar.Collapse id="navbar-nav" className="justify-content-end no-print">
          <Nav>
            {auth.user && (
              <NavDropdown id="dropdown" title="Account">
                {/* InsightSQC menu items - local links */}
                <ShowLink to="/dashboard" show={activeItems.dashboard}>
                  SQC Dashboard
                </ShowLink>
                <ShowLink to="/chart" show={activeItems.qCDataCharts}>
                  QC Data and Charts
                </ShowLink>
                <ShowLink to="/master" show={activeItems.masterData}>
                  Master Data
                </ShowLink>
                <ShowLink
                  to="/notifications"
                  show={
                    activeItems.notificationsManager &&
                    showForAdminView(permissionData, SCREEN?.NOTIFICATIONS)
                  }
                >
                  Notifications Manager
                </ShowLink>
                <NavDropdown.Divider />
                <ShowLink to="/settings/general" show={activeItems.settings}>
                  Settings
                </ShowLink>
                <NavDropdown.Divider />
                <ShowExtLink to={fuelAppUrl || '#'} show={!!fuelAppUrl}>
                  Go to Fuel Apps
                </ShowExtLink>
                <NavDropdown.Divider />
                <LinkContainer to="/auth/signout">
                  <NavDropdown.Item
                    active={false}
                    onClick={(e) => {
                      e.preventDefault();
                      auth.signout();
                    }}
                  >
                    Sign out
                  </NavDropdown.Item>
                </LinkContainer>
              </NavDropdown>
            )}

            {!auth.user && (
              <Nav.Item>
                <LinkContainer to="/auth/signin">
                  <Nav.Link active={false}>Sign in</Nav.Link>
                </LinkContainer>
              </Nav.Item>
            )}
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
}

NavbarCustom.propTypes = {
  bg: PropTypes.string,
  variant: PropTypes.string,
  expand: PropTypes.string,
  logo: PropTypes.string,
  logoAncillary: PropTypes.string
};

export default NavbarCustom;
